import { useState } from 'react';
import { CalendarCheck, Check, RotateCcw, UserRound } from 'lucide-react';
import { useStore } from '../../store/useStore';
import type { Task } from '../../types';
import { HANDED_TO_MAX } from '../../engine/insights';
import { quickDateOptions } from '../../engine/dates';
import { weeklyInsightsEnabled } from '../../native/insightsGate';

/** Check-back chips (spec §3): Tomorrow · Friday · Next week · pick a date. */
export function checkBackOptions(now: Date = new Date()): { label: string; at: number }[] {
  const quick = quickDateOptions(now);
  const tomorrow = quick[1];
  const friday = quick.find((q) => q.label === 'Fri');
  const next = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 7, 12, 0, 0, 0).getTime();
  const out: { label: string; at: number }[] = [{ label: 'Tomorrow', at: tomorrow.at }];
  if (friday) out.push({ label: 'Friday', at: friday.at });
  out.push({ label: 'Next week', at: next });
  return out;
}

function atNoonOf(dateValue: string): number | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateValue);
  if (!m) return undefined;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12, 0, 0, 0).getTime();
}

const dateLabel = (at: number) =>
  new Date(at).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });

/**
 * Zone 3 hand-off + check-back (Weekly Insights feature 2, spec §3).
 * Three states on a Zone 3 card, all web only:
 *   - nothing yet: a quiet "Hand to…" link that opens the editor;
 *   - handed off, check-back in the future: "With {name} · check back {date}"
 *     + Take it back;
 *   - check-back due: "Check in with {name}" — Done / Still with them /
 *     Take it back.
 * The check-back date is `deferredUntil`, so the Today lens already hides
 * the task until then and brings it back on the day.
 */
export function HandOffPanel({ task }: { task: Task }) {
  const editing = useStore((s) => s.handOffEditing === task.id);
  const openHandOff = useStore((s) => s.openHandOff);
  const handOff = useStore((s) => s.handOff);
  const setCheckBack = useStore((s) => s.setCheckBack);
  const takeBack = useStore((s) => s.takeBack);
  const cycleTaskState = useStore((s) => s.cycleTaskState);
  const [name, setName] = useState(task.handedTo ?? '');
  const [when, setWhen] = useState<number | undefined>(task.deferredUntil);
  const [pickOpen, setPickOpen] = useState(false);

  if (!weeklyInsightsEnabled() || task.zone !== 'zone3' || task.done) return null;

  const handed = Boolean(task.handedTo?.trim());
  const due = handed && task.deferredUntil !== undefined && task.deferredUntil <= Date.now();

  if (editing) {
    return (
      <div
        role="group"
        aria-label="Hand off this task"
        className="space-y-2 rounded-lg border border-amber-200 dark:border-amber-500/30 bg-amber-50/60 dark:bg-amber-900/10 p-2"
      >
        <label className="block text-[11px] font-bold text-slate-700 dark:text-slate-200" htmlFor={`handoff-${task.id}`}>
          Hand to…
        </label>
        <input
          id={`handoff-${task.id}`}
          type="text"
          value={name}
          maxLength={HANDED_TO_MAX}
          autoFocus
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && name.trim()) handOff(task.id, name, when);
            if (e.key === 'Escape') openHandOff(undefined);
          }}
          placeholder="A name — who has this now?"
          className="w-full rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1.5 text-base sm:text-xs text-slate-900 dark:text-white placeholder-slate-500 focus:border-amber-500 focus:outline-none"
        />
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[11px] font-bold text-slate-700 dark:text-slate-200">Check back</span>
          {checkBackOptions().map((o) => (
            <button
              key={o.label}
              type="button"
              onClick={() => setWhen(o.at)}
              className={`rounded-full border px-2 py-0.5 text-[11px] font-bold ${
                when === o.at
                  ? 'border-amber-500 bg-amber-500/15 text-amber-700 dark:text-amber-300'
                  : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-amber-300'
              }`}
            >
              {o.label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setPickOpen((p) => !p)}
            className="rounded-full border border-slate-200 dark:border-slate-700 px-2 py-0.5 text-[11px] font-bold text-slate-600 dark:text-slate-300 hover:border-amber-300"
          >
            Pick a date
          </button>
          {pickOpen && (
            <input
              type="date"
              aria-label="Check-back date"
              onChange={(e) => {
                const at = atNoonOf(e.target.value);
                if (at) setWhen(at);
              }}
              className="rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1 text-xs text-slate-900 dark:text-white"
            />
          )}
          {when !== undefined && (
            <span className="text-[11px] text-slate-500 dark:text-slate-400">→ {dateLabel(when)}</span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            disabled={!name.trim()}
            onClick={() => handOff(task.id, name, when)}
            className="rounded-md bg-amber-500 px-2.5 py-1.5 text-[11px] font-bold text-white hover:bg-amber-600 disabled:opacity-40"
          >
            Hand it off
          </button>
          <button
            type="button"
            onClick={() => openHandOff(undefined)}
            className="rounded-md border border-slate-200 dark:border-slate-700 px-2.5 py-1.5 text-[11px] font-semibold text-slate-600 dark:text-slate-300"
          >
            Cancel
          </button>
          <span className="ml-auto text-[10px] text-slate-500">It leaves Today until the check-back day.</span>
        </div>
      </div>
    );
  }

  if (due) {
    return (
      <div
        role="group"
        aria-label={`Check in with ${task.handedTo}`}
        className="space-y-1.5 rounded-lg border border-amber-300 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-900/20 p-2"
      >
        <p className="flex items-center gap-1.5 text-xs font-bold text-amber-800 dark:text-amber-200">
          <CalendarCheck size={13} /> Check in with {task.handedTo}
        </p>
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            type="button"
            onClick={() => cycleTaskState(task.id)}
            className="flex items-center gap-1 rounded-md bg-emerald-600 px-2.5 py-1.5 text-[11px] font-bold text-white hover:bg-emerald-500"
          >
            <Check size={12} /> Done
          </button>
          <button
            type="button"
            onClick={() => setCheckBack(task.id, checkBackOptions()[0].at)}
            className="rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2.5 py-1.5 text-[11px] font-bold text-slate-700 dark:text-slate-200"
          >
            Still with them (ask tomorrow)
          </button>
          <button
            type="button"
            onClick={() => takeBack(task.id)}
            className="flex items-center gap-1 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2.5 py-1.5 text-[11px] font-bold text-slate-700 dark:text-slate-200"
          >
            <RotateCcw size={12} /> Take it back
          </button>
        </div>
      </div>
    );
  }

  if (handed) {
    return (
      <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-600 dark:text-slate-300">
        <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 font-bold text-amber-800 dark:text-amber-200">
          <UserRound size={11} /> With {task.handedTo}
          {task.deferredUntil !== undefined && ` · check back ${dateLabel(task.deferredUntil)}`}
        </span>
        <button type="button" onClick={() => openHandOff(task.id)} className="font-semibold hover:text-brand-600 dark:hover:text-brand-400">
          Change
        </button>
        <button type="button" onClick={() => takeBack(task.id)} className="font-semibold hover:text-brand-600 dark:hover:text-brand-400">
          Take it back
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => openHandOff(task.id)}
      className="flex items-center gap-1 text-[11px] font-medium text-slate-400 dark:text-slate-500 hover:text-amber-600 dark:hover:text-amber-400"
    >
      <UserRound size={11} /> Hand to…
    </button>
  );
}
